import { animate, state, style, transition, trigger } from '@angular/animations';
import { Direction } from '@angular/cdk/bidi';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MarketingTextStyle, PlatformType } from '../../../common';
import { GoogleTagManagerEventAction } from '../../enums';
import { MarketingLandingGtmTrayData } from '../../interfaces/marketing-landing-gtm-tray-data.interface';
import { ClientConfigService, ImgQuery } from '../../services';
import { getImgUrlByPlatform, TypedChanges } from '../../utils';
import { EmailCollectorComponent } from '../email-collector/email-collector.component';
import { MarketingEmailCollectorViewModel } from './marketing-email-collector.view-model';

@Component({
  selector: 'osn-shared-marketing-email-collector',
  templateUrl: './marketing-email-collector.component.html',
  styleUrls: ['./marketing-email-collector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('errorAnimation', [
      state('valid', style({ marginTop: '-15px' })),
      state('invalid', style({ marginTop: '-6px' })),
      transition('valid <=> invalid', animate('0.25s')),
    ]),
  ],
})
export class MarketingEmailCollectorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public vm: MarketingEmailCollectorViewModel;
  @Output() public emailSubmitted = new EventEmitter<string>();
  @Output() public measureEmailCollectorButtonClick: EventEmitter<MarketingLandingGtmTrayData> = new EventEmitter();
  @Output() public measureLoginError: EventEmitter<void> = new EventEmitter();
  public backgroundImages: { imgQuery: ImgQuery; fallbackUrl: string }[];
  public backgroundImagePosition = 0;
  private timerHandle: ReturnType<typeof setInterval>;
  private readonly EMAIL_COLLECTOR_SCROLL_INTERVAL = this.clientConfigService.marketingEmailCollectorScrollInterval;

  // Needed for other marketing trays to be able to scroll to the email collector element.
  @ViewChild(EmailCollectorComponent) public emailCollectorComponent: EmailCollectorComponent;

  public readonly termsAndConditionsLink =
    this.translateService.currentLang === 'en'
      ? this.clientConfigService.termsAndConditionsUrlEn
      : this.clientConfigService.termsAndConditionsUrlAr;
  public readonly privacyPolicyLink =
    this.translateService.currentLang === 'en' ? this.clientConfigService.privacyPolicyEn : this.clientConfigService.privacyPolicyAr;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly cdr: ChangeDetectorRef,
    private readonly clientConfigService: ClientConfigService,
    private readonly translateService: TranslateService,
    @Inject(PLATFORM_ID) private readonly platformId
  ) {}

  public ngOnInit() {
    this.initBackgroundImages();

    if (isPlatformBrowser(this.platformId)) {
      this.timerHandle = setInterval(() => {
        this.advanceBackgroundImage();
        this.cdr.detectChanges();
      }, this.EMAIL_COLLECTOR_SCROLL_INTERVAL);
    }
  }

  public ngOnChanges(changes: TypedChanges<Pick<MarketingEmailCollectorComponent, 'vm'>>): void {
    if (changes.vm && this.vm) {
      this.initBackgroundImages();
    }
  }

  public ngOnDestroy(): void {
    clearInterval(this.timerHandle);
  }

  public get documentDir(): Direction {
    return this.document.dir === 'rtl' ? 'rtl' : 'ltr';
  }

  public get isLightText(): boolean {
    return this.vm.textStyle === MarketingTextStyle.LIGHT;
  }

  public onEmailCollectorButtonClick(): void {
    this.measureEmailCollectorButtonClick.emit({
      buttonLabel: this.vm.buttonLabelEnglish,
      type: GoogleTagManagerEventAction.MainCtaClick,
    });
  }

  public onLoginError(): void {
    this.measureLoginError.emit();
  }

  private initBackgroundImages() {
    this.backgroundImages = this.vm.backgroundImages.map((backgroundImage) => {
      const mobileImg = getImgUrlByPlatform(PlatformType.Mobile, backgroundImage.urlsByPlatform);
      const tabletImg = getImgUrlByPlatform(PlatformType.Tablet, backgroundImage.urlsByPlatform);
      const desktopImg = getImgUrlByPlatform(PlatformType.Web, backgroundImage.urlsByPlatform);
      return {
        imgQuery: {
          XS: { url: mobileImg },
          SM: { url: tabletImg },
          MD: { url: desktopImg },
          LG: { url: desktopImg },
          XL: { url: desktopImg },
        },
        fallbackUrl: backgroundImage.fallbackUrl,
      };
    });
  }

  private advanceBackgroundImage(): void {
    if (this.backgroundImagePosition === this.backgroundImages.length - 1) {
      this.backgroundImagePosition = 0;
    } else {
      this.backgroundImagePosition = this.backgroundImagePosition + 1;
    }
    this.cdr.detectChanges();
  }
}
